import { useEffect, useState } from "react";

/**
 * useExitIntent
 *
 * Detects when a user is likely about to leave the page.
 * - Desktop: mouse leaves the top of the viewport.
 * - Mobile: rapid upward scroll past the hero (heuristic for back-button intent).
 *
 * Throttled aggressively:
 * - Persists dismissal in localStorage for 14 days (cross-session, cross-page).
 * - Won't arm until the user has been on the site for MIN_DWELL_MS.
 * - Requires meaningful scroll engagement before firing.
 */
const STORAGE_KEY = "exitIntentDismissedAt";
const COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000; // 14 days
const MIN_DWELL_MS = 25_000; // user must spend 25s on page first
const SESSION_KEY = "exitIntentSeenSession";

export function useExitIntent({ enabled = true }: { enabled?: boolean } = {}) {
  const [triggered, setTriggered] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;

    // Per-session guard (don't refire within the same tab session)
    try {
      if (sessionStorage.getItem(SESSION_KEY) === "1") return;
    } catch {
      /* ignore */
    }

    // Cross-session cooldown
    try {
      const dismissedAtRaw = localStorage.getItem(STORAGE_KEY);
      if (dismissedAtRaw) {
        const dismissedAt = parseInt(dismissedAtRaw, 10);
        if (!Number.isNaN(dismissedAt) && Date.now() - dismissedAt < COOLDOWN_MS) {
          return;
        }
      }
    } catch {
      /* ignore */
    }

    let armed = false;
    let lastY = window.scrollY;
    let lastT = performance.now();

    const onMouseOut = (e: MouseEvent) => {
      if (!armed) return;
      // Triggered when mouse leaves through the top of the viewport
      if (e.clientY <= 0 && !e.relatedTarget) {
        fire();
      }
    };

    const onScroll = () => {
      if (!armed) return;
      const now = performance.now();
      const y = window.scrollY;
      const dy = lastY - y; // positive = scrolling up
      const dt = now - lastT || 1;
      const velocity = dy / dt; // px per ms

      // Require deeper engagement and a sharper upward fling
      if (lastY > 1200 && velocity > 2.2) {
        fire();
      }
      lastY = y;
      lastT = now;
    };

    const fire = () => {
      setTriggered(true);
      try {
        sessionStorage.setItem(SESSION_KEY, "1");
        localStorage.setItem(STORAGE_KEY, String(Date.now()));
      } catch {
        /* ignore */
      }
      cleanup();
    };

    const cleanup = () => {
      document.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("scroll", onScroll);
    };

    // Arm only after the user has dwelled long enough
    const armTimer = window.setTimeout(() => {
      armed = true;
      lastY = window.scrollY;
      lastT = performance.now();
      document.addEventListener("mouseout", onMouseOut);
      window.addEventListener("scroll", onScroll, { passive: true });
    }, MIN_DWELL_MS);

    // Safety timeout — disable after 5 minutes to avoid late surprise popups
    const timer = window.setTimeout(cleanup, 5 * 60 * 1000);

    return () => {
      cleanup();
      window.clearTimeout(armTimer);
      window.clearTimeout(timer);
    };
  }, [enabled]);

  return {
    triggered,
    dismiss: () => {
      setTriggered(false);
      try {
        localStorage.setItem(STORAGE_KEY, String(Date.now()));
        sessionStorage.setItem(SESSION_KEY, "1");
      } catch {
        /* ignore */
      }
    },
  };
}
